import { Check as CheckIcon } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';

import { isRFQCompileActive, useRFQCompile } from '@/context/RFQCompileProvider';

/**
 * Compact processing wheel for RFQ generation + compile. Hidden when idle.
 * Intended for top-right of the Punch List page and the HomeHub punch list widget.
 */
export const RFQCompileIndicator = () => {
  const { progress } = useRFQCompile();
  const active = isRFQCompileActive(progress);
  const isDone = progress.phase === 'done';
  const isError = progress.phase === 'error';

  if (!active && !isDone && !isError) return null;

  const label = (() => {
    if (isError) return progress.error ?? 'RFQ error';
    if (isDone) return 'RFQ ready';
    if (progress.phase === 'compiling') return 'Compiling PDF…';
    if (progress.total > 1) return `Generating RFQ ${progress.done + 1} of ${progress.total}…`;
    return 'Generating RFQ…';
  })();

  const color = isError ? '#DB5282' : isDone ? '#C0FF02' : '#C0FF02';
  const ringValue = progress.total === 0 ? 0 : (progress.done / progress.total) * 100;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 10px',
        borderRadius: '6px',
        background: 'rgba(192,255,2,0.06)',
        border: `1px solid ${isError ? 'rgba(219,82,130,0.35)' : 'rgba(192,255,2,0.25)'}`,
      }}>
      {isDone ? (
        <CheckIcon sx={{ fontSize: 14, color }} />
      ) : (
        <Box sx={{ position: 'relative', width: 14, height: 14, flexShrink: 0 }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={14}
            thickness={5}
            sx={{ color: 'rgba(192,255,2,0.15)', position: 'absolute', top: 0, left: 0 }}
          />
          <CircularProgress
            variant={progress.phase === 'compiling' ? 'indeterminate' : 'determinate'}
            value={ringValue}
            size={14}
            thickness={5}
            sx={{ color, position: 'absolute', top: 0, left: 0 }}
          />
        </Box>
      )}
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          color: isError ? '#DB5282' : 'var(--tx-text)',
          fontFamily: "'Manrope',sans-serif",
          whiteSpace: 'nowrap',
        }}>
        {label}
      </Typography>
    </Box>
  );
};
